import React, { useState, useMemo, useCallback } from 'react';
import {
  Shield, CheckCircle, AlertTriangle, CreditCard, FileText,
  ChevronRight, BookOpen, User, Clock, PhoneCall,
  MessageSquare, Check
} from 'lucide-react';
import TranscriptDisplay from '../../customerServiceDashboard/components/TranscriptDisplay';

/**
 * AgentAssistDesktop — Phase 4: Fraud Specialist + Agent Assist
 *
 * Left panel:  Live transcription via the socket transcription service.
 * Right panel: Agent desktop — Member card, Journey timeline, Risk event,
 *              Next Best Actions (auto-completed from transcript keywords),
 *              Knowledge Assist.
 */

// ─── NBA items ────────────────────────────────────────────────────────────────

const NBA_ITEMS = [
  { id: 'confirm',  label: 'Confirm disputed charge',       icon: AlertTriangle, color: 'red',    keywords: ['not mine', 'did not authorize', "didn't authorize", 'unauthorized', 'dispute', 'fraud'] },
  { id: 'freeze',   label: 'Freeze / block debit card',     icon: CreditCard,    color: 'orange', keywords: ['block', 'freeze', 'blocked', 'block the card'] },
  { id: 'dispute',  label: 'Review pending transactions',   icon: FileText,      color: 'amber',  keywords: ['pending', 'pending transaction', 'pending auth'] },
  { id: 'replace',  label: 'Order replacement card',        icon: CreditCard,    color: 'blue',   keywords: ['replacement', 'new card', 'replace', 'reissue', 'delivery'] },
  { id: 'claim',    label: 'Initiate fraud claim',          icon: Shield,        color: 'purple', keywords: ['claim', 'fraud claim', 'dispute submitted', 'opened dispute'] },
  { id: 'wallet',   label: 'Offer digital wallet guidance', icon: MessageSquare, color: 'sky',    keywords: ['recurring', 'digital wallet', 'apple pay', 'google pay', 'gym', 'streaming'] },
];

const COLOR_MAP = {
  red:    { bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-200',    icon: 'text-red-500'    },
  orange: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', icon: 'text-orange-500' },
  amber:  { bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-200',  icon: 'text-amber-500'  },
  blue:   { bg: 'bg-blue-50',   text: 'text-primaryDark',   border: 'border-blue-200',   icon: 'text-primary'   },
  purple: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', icon: 'text-purple-500' },
  sky:    { bg: 'bg-sky-50',    text: 'text-sky-700',    border: 'border-sky-200',    icon: 'text-sky-500'    },
};

// ─── Knowledge Assist articles ─────────────────────────────────────────────────

const KNOWLEDGE = [
  { title: 'Fraud Dispute Workflow',    desc: 'Step-by-step Reg E dispute process and timelines' },
  { title: 'Card Block / Reissue',      desc: 'Procedure for blocking and ordering replacement cards' },
  { title: 'Regulation E Disclosure',   desc: 'Required member disclosures for unauthorized transactions' },
  { title: 'Empathy Statement Library', desc: 'Approved language for high-stress member interactions' },
];

// ─── Journey timeline ──────────────────────────────────────────────────────────

const JOURNEY = [
  { label: 'Web Chat',       icon: MessageSquare, done: true,  active: false },
  { label: 'Voice Callback', icon: PhoneCall,     done: true,  active: false },
  { label: 'Fraud Detected', icon: AlertTriangle, done: true,  active: false },
  { label: 'Fraud Specialist', icon: User,        done: false, active: true  },
];

// ─── Component ────────────────────────────────────────────────────────────────

const AgentAssistDesktop = ({ onTranscriptUpdate }) => {
  const [liveTranscript, setLiveTranscript] = useState('');

  const handleTranscriptUpdate = useCallback((text) => {
    setLiveTranscript(text);
    onTranscriptUpdate?.(text);
  }, [onTranscriptUpdate]);

  // Auto-complete NBA items based on keywords found in the live transcript
  const completedNBA = useMemo(() => {
    const lower = liveTranscript.toLowerCase();
    const completed = new Set();
    NBA_ITEMS.forEach(item => {
      if (item.keywords.some(kw => lower.includes(kw))) {
        completed.add(item.id);
      }
    });
    return completed;
  }, [liveTranscript]);

  return (
    <div className="h-full flex gap-4 min-h-0">
      {/* ── LEFT: Live Transcription ── */}
      <div className="w-[42%] flex flex-col min-h-0 overflow-hidden">
        <TranscriptDisplay
          onTranscriptionUpdate={handleTranscriptUpdate}
          onCallStatusChange={() => {}}
        />
      </div>

      {/* ── RIGHT: Agent Desktop ── */}
      <div className="flex-1 overflow-y-auto space-y-3 min-h-0 pb-2 pr-1 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-gray-50 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">

        {/* Member Card */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#1a3a6b] flex items-center justify-center text-white font-bold text-sm">
                JW
              </div>
              <div>
                <p className="font-semibold text-gray-900">Jamie Wallace</p>
                <p className="text-xs text-gray-500">Member since 2019 · Account ending 1842</p>
              </div>
            </div>
            <span className="flex items-center gap-1 text-xs bg-green-50 text-green-700 border border-green-200 px-2 py-1 rounded-full font-medium">
              <CheckCircle className="w-3 h-3" /> Verified
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="bg-gray-50 rounded-xl p-2.5 text-center">
              <p className="text-gray-500 mb-0.5">Auth method</p>
              <p className="font-semibold text-gray-800">Web chat + Voice</p>
            </div>
            <div className="bg-red-50 rounded-xl p-2.5 text-center">
              <p className="text-red-500 mb-0.5">Sentiment</p>
              <p className="font-semibold text-red-700">Concerned / Urgent</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-2.5 text-center">
              <p className="text-primary mb-0.5">Channels</p>
              <p className="font-semibold text-primaryDark">Chat → Voice → Agent</p>
            </div>
          </div>
        </div>

        {/* Journey Timeline */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> Journey Summary
          </p>
          <div className="flex items-center gap-1">
            {JOURNEY.map((j, i) => {
              const Icon = j.icon;
              return (
                <React.Fragment key={j.label}>
                  <div className={`flex flex-col items-center gap-1 flex-1 min-w-0 ${j.active ? 'opacity-100' : j.done ? 'opacity-80' : 'opacity-40'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                      j.active ? 'bg-[#1a3a6b] border-[#1a3a6b] text-white' :
                      j.done   ? 'bg-green-500 border-green-500 text-white' :
                                 'bg-gray-100 border-gray-300 text-gray-400'
                    }`}>
                      {j.done && !j.active ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                    </div>
                    <p className="text-[10px] text-center text-gray-600 leading-tight font-medium">{j.label}</p>
                  </div>
                  {i < JOURNEY.length - 1 && (
                    <div className={`flex-1 h-0.5 mb-4 max-w-8 ${j.done ? 'bg-green-400' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Risk Event */}
        <div className="bg-red-50 rounded-2xl border border-red-200 shadow-sm p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-red-700 mb-1">Fraud Risk Event</p>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-red-600">Merchant</span>
                  <span className="font-medium text-red-800">Electro World Online</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-red-600">Amount</span>
                  <span className="font-medium text-red-800">$742.63</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-red-600">Pending auth</span>
                  <span className="font-medium text-red-800">$11.99 (same merchant)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-red-600">Member response</span>
                  <span className="font-medium text-red-800">"I did not authorize this"</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-red-600">Detection</span>
                  <span className="font-medium text-red-800">Outside spending pattern / location</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Next Best Actions */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
            <ChevronRight className="w-3.5 h-3.5" /> Next Best Actions
          </p>
          <div className="space-y-2">
            {NBA_ITEMS.map(item => {
              const c = COLOR_MAP[item.color];
              const Icon = item.icon;
              const done = completedNBA.has(item.id);
              return (
                <div
                  key={item.id}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border text-sm transition-all ${
                    done
                      ? 'bg-green-50 border-green-200 opacity-70'
                      : `${c.bg} ${c.border} border`
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${done ? 'bg-green-500' : c.bg}`}>
                    {done
                      ? <Check className="w-3 h-3 text-white" />
                      : <Icon className={`w-3 h-3 ${c.icon}`} />
                    }
                  </div>
                  <span className={`flex-1 font-medium ${done ? 'text-green-700 line-through' : c.text}`}>
                    {item.label}
                  </span>
                  {done && <span className="text-xs text-green-600 font-medium">Done</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Knowledge Assist */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
            <BookOpen className="w-3.5 h-3.5" /> Knowledge Assist
          </p>
          <div className="space-y-2">
            {KNOWLEDGE.map(k => (
              <div
                key={k.title}
                className="flex items-start gap-3 p-3 rounded-xl bg-blue-50 border border-blue-100 cursor-pointer hover:bg-blue-100 transition-colors"
              >
                <FileText className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-primaryDark">{k.title}</p>
                  <p className="text-xs text-primaryDark mt-0.5">{k.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentAssistDesktop;
